/* eslint-disable no-param-reassign */
import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {Toast, AppState} from 'packages/common-types/base-types';
import {Tournament, User} from 'packages/common-types/schema-types';
import type {RootState} from 'redux/store';

// Define the initial state using that type
const initialState: AppState = {
  toastOpen: null,
  playingIds: [],
  selectedTournament: null,
  selectedStanding: null,
  currentGroup: [],
  sponsorList: [],
};

export const appSlice = createSlice({
  name: 'app',
  // `createSlice` will infer the state type from the `initialState` argument
  initialState,
  reducers: {
    addPlayingTournaments: (state, action: PayloadAction<string>) => {
      state.playingIds = [...state.playingIds, action.payload];
    },
    removePlayingTournaments: (state, action: PayloadAction<string>) => {
      state.playingIds = state.playingIds.filter((id) => id !== action.payload);
    },
    setSelectedTournament: (state, action: PayloadAction<Tournament>) => {
      state.selectedTournament = action.payload;
    },
    setSelectedStanding: (state, action: PayloadAction<Tournament>) => {
      state.selectedStanding = action.payload;
    },
    triggerToast: (state, action: PayloadAction<Toast>) => {
      state.toastOpen = action.payload;
    },
    addPlayerToGroup: (state, action: PayloadAction<User>) => {
      state.currentGroup = [...state.currentGroup, action.payload];
    },
    removePlayerFromGroup: (state, action: PayloadAction<string>) => {
      state.currentGroup = state.currentGroup.filter(
        (user) => user.uid !== action.payload
      );
    },
    setFileBlob: (state, action: PayloadAction<string>) => {
      state.sponsorList.push(action.payload);
    },
    deleteSponsor: (state, action: PayloadAction<string>) => {
      state.sponsorList = state.sponsorList.filter(
        (sponsor) => sponsor !== action.payload
      );
    },
    clearGroup: (state) => {
      state.currentGroup = [];
    },
  },
});

export const {
  addPlayingTournaments,
  removePlayingTournaments,
  setSelectedTournament,
  addPlayerToGroup,
  removePlayerFromGroup,
  triggerToast,
  clearGroup,
  setFileBlob,
  deleteSponsor,
  setSelectedStanding,
} = appSlice.actions;

// Other code such as selectors can use the imported `RootState` type
export const selectToast = (state: RootState) => state.app.toastOpen;

export const selectPlayingTournaments = (state: RootState) =>
  state.app.playingIds;

export const selectTournament = (state: RootState) =>
  state.app.selectedTournament;

export const selectStanding = (state: RootState) => state.app.selectedStanding;

export const selectPlayersToGroup = (state: RootState) =>
  state.app.currentGroup;

export const selectFileBlob = (state: RootState) => state.app.sponsorList;

export default appSlice.reducer;
